import styled from "styled-components";
import { ClipLoaderCommon } from "@components";
import { VisibilityOffOutlined, VisibilityOutlined } from "@mui/icons-material";
import { theme } from "@utilities";

interface GuestLoginModalProps {
    email: string;
    password: string;
    onPasswordChange: (value: string) => void;
    showPassword: boolean;
    onToggleShowPassword: () => void;
    isLoggingIn: boolean;
    onLogin: () => void;
    onClose: () => void;
    onForgotPassword?: () => void;
}

export const GuestLoginModal = ({
    email,
    password,
    onPasswordChange,
    showPassword,
    onToggleShowPassword,
    isLoggingIn,
    onLogin,
    onClose,
    onForgotPassword,
}: GuestLoginModalProps) => {
    return (
        <ModalOverlay onClick={onClose}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
                <Header>
                    <h3>Account Found</h3>
                    <CloseBtn onClick={onClose}>&times;</CloseBtn>
                </Header>
                <Description>
                    An account with <strong>{email}</strong> already exists. Please log in to continue with your booking.
                </Description>
                <Field>
                    <label>Email</label>
                    <input type="email" value={email} disabled />
                </Field>
                <Field>
                    <label>Password</label>
                    <PasswordWrapper>
                        <input
                            type={showPassword ? "text" : "password"}
                            placeholder="Enter your password"
                            value={password}
                            onChange={(e) => onPasswordChange(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") onLogin();
                            }}
                            autoFocus
                        />
                        <EyeIcon onClick={onToggleShowPassword}>
                            {showPassword ? <VisibilityOutlined /> : <VisibilityOffOutlined />}
                        </EyeIcon>
                    </PasswordWrapper>
                </Field>
                {onForgotPassword && (
                    <ForgotPasswordRow>
                        <ForgotPasswordLink onClick={onForgotPassword}>
                            Forgot password?
                        </ForgotPasswordLink>
                    </ForgotPasswordRow>
                )}
                <LoginButton onClick={onLogin} disabled={isLoggingIn || !password}>
                    {isLoggingIn ? (
                        <ClipLoaderCommon color="#fff" size={20} margin={0} />
                    ) : (
                        "Log In & Continue"
                    )}
                </LoginButton>
            </ModalContent>
        </ModalOverlay>
    );
};

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
`;

const ModalContent = styled.div`
    background: white;
    border-radius: 12px;
    padding: 28px;
    width: 90%;
    max-width: 420px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);

    h3 {
        margin: 0;
        font-size: 18px;
        font-weight: 600;
        color: ${theme.colors.black};
    }
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
`;

const CloseBtn = styled.button`
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: ${theme.colors.gray_3};
    padding: 0;
    line-height: 1;
`;

const Description = styled.p`
    color: ${theme.colors.gray_3};
    font-size: 14px;
    margin: 0 0 16px;
`;

const Field = styled.div`
    margin-bottom: 14px;

    label {
        display: block;
        font-size: 13px;
        font-weight: 500;
        color: ${theme.colors.gray_3};
        margin-bottom: 6px;
    }

    input {
        width: 100%;
        padding: 10px 12px;
        border: 1px solid ${theme.colors.gray_6};
        border-radius: 6px;
        font-size: 14px;
        font-family: inherit;
        outline: none;
        box-sizing: border-box;

        &:focus {
            border-color: ${theme.colors.highlight};
        }

        &:disabled {
            background-color: #f5f5f5;
            color: ${theme.colors.gray_3};
        }
    }
`;

const PasswordWrapper = styled.div`
    position: relative;

    input {
        padding-right: 40px;
    }
`;

const EyeIcon = styled.span`
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
    color: ${theme.colors.gray_3};
    display: flex;
    align-items: center;

    svg {
        width: 20px;
        height: 20px;
    }
`;

const ForgotPasswordRow = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-bottom: 4px;
`;

const ForgotPasswordLink = styled.button`
    background: none;
    border: none;
    color: ${theme.colors.highlight};
    font-size: 13px;
    font-family: inherit;
    cursor: pointer;
    padding: 0;
    text-decoration: underline;

    &:hover {
        opacity: 0.8;
    }
`;

const LoginButton = styled.button<{ disabled?: boolean }>`
    width: 100%;
    padding: 12px;
    background: ${(p) => (p.disabled ? theme.colors.gray_6 : theme.gradients.button)};
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 15px;
    font-weight: 600;
    font-family: inherit;
    cursor: ${(p) => (p.disabled ? "not-allowed" : "pointer")};
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 8px;
`;
